import { useEffect, useState, useCallback } from 'react';
import type { Session } from '@supabase/supabase-js';
import { toast as message } from 'sonner';
import { supabaseClient } from '../../utils/supabase';
import { GOOGLE_DRIVE_SCOPE } from './drive';
import useAuth from '../../utils/useAuth';

/**
 * Google access-token management tied to the logged-in user.
 *
 * Supabase only exposes provider_token / provider_refresh_token on the session
 * produced by the OAuth callback — they are dropped on session refresh (~1h).
 * We capture them per user, and mint fresh access tokens through the
 * gmail-xt-google-token edge function (which holds the Google client secret).
 */

interface CachedAccess {
  token: string;
  /** epoch ms after which the token must not be used */
  exp: number;
}

const refreshKey = (uid: string) => `sky_google_refresh_${uid}`;
const accessKey = (uid: string) => `sky_google_access_${uid}`;
// Google access tokens live ~60 min; refresh a bit early.
const ASSUMED_TTL_MS = 55 * 60 * 1000;
const EXPIRY_SLACK_MS = 60 * 1000;

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage blocked — degrade to session-only tokens
  }
}

function clearCachedGoogleTokens(uid: string): void {
  try {
    localStorage.removeItem(refreshKey(uid));
    localStorage.removeItem(accessKey(uid));
  } catch {
    // Storage may be blocked; the OAuth redirect still replaces session tokens.
  }
}

/** Persist any Google tokens present on a fresh OAuth session. */
export function captureSessionGoogleTokens(session: Session | null): void {
  const uid = session?.user?.id;
  if (!uid) return;
  if (session.provider_refresh_token) {
    writeJson(refreshKey(uid), session.provider_refresh_token);
  }
  if (session.provider_token) {
    const cached = readJson<CachedAccess>(accessKey(uid));
    if (!cached || cached.token !== session.provider_token) {
      writeJson(accessKey(uid), { token: session.provider_token, exp: Date.now() + ASSUMED_TTL_MS });
    }
  }
}

/** Returns a currently-valid Google access token for the user, refreshing via
 * the edge function when possible. Null = not connected (needs OAuth). */
export async function getGoogleAccessToken(session: Session | null): Promise<string | null> {
  const uid = session?.user?.id;
  if (!uid) return null;

  captureSessionGoogleTokens(session);

  const cached = readJson<CachedAccess>(accessKey(uid));
  if (cached?.token && cached.exp > Date.now() + EXPIRY_SLACK_MS) {
    return cached.token;
  }

  const refreshToken = session?.provider_refresh_token || readJson<string>(refreshKey(uid));
  if (!refreshToken) return null;

  const { data, error } = await supabaseClient.functions.invoke('gmail-xt-google-token', {
    body: { refreshToken },
  });
  if (error || !data?.accessToken) {
    console.warn('Google token refresh failed:', error?.message || data?.error);
    return null;
  }
  const ttlMs = (typeof data.expiresIn === 'number' ? data.expiresIn * 1000 : ASSUMED_TTL_MS);
  writeJson(accessKey(uid), { token: data.accessToken, exp: Date.now() + ttlMs - EXPIRY_SLACK_MS });
  return data.accessToken as string;
}

/** Re-run Google OAuth with Drive scope and an explicit account chooser.
 *
 * The CRM login is Google-only, so reconnecting must refresh the existing
 * provider session rather than trying to link a second identity. linkIdentity
 * fails when the Google identity already belongs to the signed-in user and
 * leaves expired or wrong-account Drive tokens in place.
 */
export async function connectGoogleDrive(): Promise<void> {
  const { data: { session } } = await supabaseClient.auth.getSession();
  if (session?.user.id) clearCachedGoogleTokens(session.user.id);

  const options = {
    redirectTo: window.location.href,
    scopes: GOOGLE_DRIVE_SCOPE,
    queryParams: {
      access_type: 'offline',
      prompt: 'consent select_account',
    },
  };

  const { error } = await supabaseClient.auth.signInWithOAuth({ provider: 'google', options });
  if (error) {
    console.error('Google Drive reconnect failed:', error.message);
    message.error('Could not reconnect Google Drive. Please try again.');
  }
}

/** Hook: valid Google access token for the logged-in user, auto-refreshed.
 * `token` null + `loading` false = show a connect button. */
export function useGoogleDriveToken(): { token: string | null; loading: boolean; connect: () => Promise<void> } {
  const { session } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const t = await getGoogleAccessToken(session);
      if (!cancelled) {
        setToken(t);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [session]);

  const connect = useCallback(() => connectGoogleDrive(), []);
  return { token, loading, connect };
}
